import { daysBetween, formatNaira, greetingFor, parseDateOnly } from "../../../utils/dashboardFormat";

// Re-exported so the page and its tests keep one import path.
export { formatNaira, greetingFor, parseDateOnly };

/**
 * Turns the combined /members/dashboard response into what the member home page shows.
 * Pure functions, no React, so the rules are easy to test.
 */

/** The dashboard query already unwraps one `data`; older code read a second one. Accept both. */
export function unwrapMemberDashboard(data) {
  if (!data || typeof data !== "object") return null;
  const looksLikePayload = (d) =>
    d && typeof d === "object" && ("summary" in d || "totalContribution" in d || "repayments" in d);
  if (looksLikePayload(data)) return data;
  if (looksLikePayload(data.data)) return data.data;
  return data;
}

/** True once the backend sends the combined fields. If not, the page falls back to the old layout. */
export function hasMemberHomeContract(dashboard) {
  return Boolean(dashboard && dashboard.summary && dashboard.loans && dashboard.repayments);
}

export function formatDueDate(value) {
  const date = parseDateOnly(value);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
}

export function describeDays(days) {
  if (days === null || days === undefined || Number.isNaN(days)) return "";
  if (days < 0) {
    const late = Math.abs(days);
    return `${late} ${late === 1 ? "day" : "days"} late`;
  }
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due in ${days} days`;
}

/**
 * What the top card should say.
 * kind: "overdue" | "today" | "upcoming" | "none"
 */
export function buildNextPayment(repayments, today = new Date()) {
  const overdue = repayments?.overdue;
  const next = repayments?.next_due || null;
  const overdueCount = Number(overdue?.count) || 0;

  if (overdueCount === 0 && !next) {
    return { kind: "none" };
  }

  const dueDate = next?.due_date || null;
  let days = next?.days_until;
  if ((days === undefined || days === null) && dueDate) {
    const parsed = parseDateOnly(dueDate);
    days = parsed ? daysBetween(today, parsed) : null;
  }

  const isOverdue = overdueCount > 0 || next?.status === "OVERDUE";
  const kind = isOverdue ? "overdue" : next?.status === "DUE_TODAY" || days === 0 ? "today" : "upcoming";

  const amount = isOverdue && overdueCount > 0 ? Number(overdue?.amount) || 0 : Number(next?.amount) || 0;

  return {
    kind,
    amount,
    overdueCount,
    loanTypeName: next?.loan_type_name || "",
    loanRequestId: next?.loan_request_id ?? null,
    dueDateLabel: formatDueDate(dueDate),
    daysLabel: describeDays(days),
    days,
  };
}

/** Rows for the "Your loans" card. Counts come from the API, amounts are shown when present. */
export function buildLoanRows(loans) {
  const money = (v) => (Number(v) > 0 ? formatNaira(v) : "");
  return [
    {
      key: "pending",
      title: "Pending applications",
      count: Number(loans?.pending_count) || 0,
      subtitle: money(loans?.pending_amount) || "Waiting for a decision",
      tone: "warn",
    },
    {
      key: "approved",
      title: "Approved",
      count: Number(loans?.approved_count) || 0,
      subtitle: money(loans?.approved_amount) || "Waiting for payout",
      tone: "ok",
    },
    {
      key: "active",
      title: "Active loans",
      count: Number(loans?.active_count) || 0,
      subtitle: Number(loans?.active_balance) > 0 ? `${formatNaira(loans.active_balance)} still owed` : "Nothing owed",
      tone: "",
    },
  ];
}

export function borrowingNote(borrowing) {
  const m = Number(borrowing?.multiplier);
  if (!Number.isFinite(m) || m <= 0) return "Based on your savings and shares";
  return `${Number(m.toFixed(2))}x your savings and shares`;
}

export function firstNameOf(dashboard, user) {
  const full = dashboard?.member?.name || user?.name || user?.first_name || "";
  return String(full).trim().split(/\s+/)[0] || "";
}
